import { useMemo, useState } from "react";
import {
  AlertTriangle, Check, ChevronDown, Pencil, Plus, Tags, Trash2, Users, X,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import {
  ActionButton,
  CustomerPanel,
  MetricCard,
  StatusBadge,
} from "./customer/CustomerUI";

const sameTags = (left, right) =>
  [...left].map((value) => value.toLowerCase()).sort().join("|") ===
  [...right].map((value) => value.toLowerCase()).sort().join("|");

export default function TagCenterCard({ finding, index, connectionId, scanId, onApplied }) {
  const [open, setOpen] = useState(false);
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [rename, setRename] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [confirmation, setConfirmation] = useState("");
  const [contactEdit, setContactEdit] = useState(null);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/connections/${connectionId}/tag-center`, {
        params: { limit: 500 },
      });
      setInventory(data);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next && !inventory && !loading) await load();
  };

  const createTag = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await api.post(`/connections/${connectionId}/tag-center/catalog`, {
        name: newName.trim(),
      });
      toast.success("Created in the CRM tag catalog.");
      setNewName("");
      await load();
      onApplied?.();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const saveRename = async () => {
    if (!rename?.name?.trim()) return;
    setSaving(true);
    try {
      await api.patch(
        `/connections/${connectionId}/tag-center/catalog/${rename.id}`,
        { name: rename.name.trim() },
      );
      toast.success("Renamed in the CRM catalog.");
      setRename(null);
      await load();
      onApplied?.();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setSaving(true);
    try {
      await api.delete(
        `/connections/${connectionId}/tag-center/catalog/${deleting.id}`,
        { data: { confirmation } },
      );
      toast.success("Deleted from the CRM catalog.");
      setDeleting(null);
      setConfirmation("");
      await load();
      onApplied?.();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleContactTag = (name) => {
    if (!contactEdit) return;
    const exists = contactEdit.tags.some((tag) => tag.toLowerCase() === name.toLowerCase());
    setContactEdit({
      ...contactEdit,
      tags: exists
        ? contactEdit.tags.filter((tag) => tag.toLowerCase() !== name.toLowerCase())
        : [...contactEdit.tags, name],
    });
  };

  const saveContactTags = async () => {
    if (!contactEdit || sameTags(contactEdit.tags, contactEdit.previousTags)) return;
    setSaving(true);
    try {
      await api.put(
        `/connections/${connectionId}/tag-center/contacts/${contactEdit.contact_id}`,
        {
          previous_tags: contactEdit.previousTags,
          tags: contactEdit.tags,
          scan_id: scanId,
        },
      );
      toast.success("Approved tag changes were written to the CRM.");
      setContactEdit(null);
      await load();
      onApplied?.();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const filteredContacts = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return inventory?.contacts || [];
    return (inventory?.contacts || []).filter((contact) =>
      [contact.name, contact.email, ...(contact.tags || [])]
        .join(" ").toLowerCase().includes(needle),
    );
  }, [inventory, search]);

  const label = inventory?.center_label || "Tag & Label Center";
  const noun = inventory?.item_noun || "tag";
  const tagCount = inventory?.catalog?.length ?? finding?.affected_count ?? 0;

  return (
    <CustomerPanel data-testid="tag-center-card"
         className="animate-fade-up border-l-4 border-l-brand"
         style={{ animationDelay: `${index * 45}ms` }}>
      <button type="button" onClick={toggleOpen}
              aria-expanded={open}
              className="flex min-h-touch w-full items-start justify-between gap-4 rounded-customer p-4 text-left transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-brand">
              Hygiene / Operations
            </span>
            <StatusBadge tone="info">
              <Tags className="h-3 w-3" /> Pulls current CRM taxonomy
            </StatusBadge>
            <StatusBadge tone="warning">
              <Check className="h-3 w-3" /> Approval required
            </StatusBadge>
          </div>
          <h3 className="mt-1 text-base font-semibold text-slate-900">{label}</h3>
          <p className="mt-0.5 text-sm text-slate-500">
            Inventory current {noun}s, review usage and suggestions, then approve catalog or contact-level changes.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <div className="text-right">
            <div className="font-mono text-2xl font-bold text-slate-900">{tagCount}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
              current {noun}s
            </div>
          </div>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {open && (
        <div className="border-t border-border">
          {loading && !inventory ? (
            <div className="p-8 text-center text-sm text-slate-500">
              Pulling the current {noun} catalog and contact assignments from the CRM…
            </div>
          ) : inventory && (
            <>
              <div className="grid gap-ui-3 border-b border-border bg-slate-50 p-ui-4 sm:grid-cols-3">
                <MetricCard label={`Catalog ${noun}s`} value={inventory.catalog.length} />
                <MetricCard label="Tagged contacts" value={inventory.tagged_contacts} />
                <MetricCard label="Contacts inspected" value={inventory.contacts_total} />
              </div>

              {(inventory.suggestions.similar_names.length > 0 ||
                inventory.suggestions.unused.length > 0 ||
                inventory.suggestions.duplicate_assignments.length > 0) && (
                <div className="border-b border-amber-200 bg-amber-50 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                    <AlertTriangle className="h-4 w-4" /> CleanIQ suggestions
                  </div>
                  <ul className="mt-2 space-y-1 text-xs text-amber-900">
                    {inventory.suggestions.similar_names.length > 0 && (
                      <li>{inventory.suggestions.similar_names.length} naming collision(s) may split workflows or reporting.</li>
                    )}
                    {inventory.suggestions.unused.length > 0 && (
                      <li>{inventory.suggestions.unused.length} unused {noun}(s) should be reviewed before deletion.</li>
                    )}
                    {inventory.suggestions.duplicate_assignments.length > 0 && (
                      <li>{inventory.suggestions.duplicate_assignments.length} contact(s) carry duplicate assignments.</li>
                    )}
                  </ul>
                </div>
              )}

              <div className="grid gap-5 p-4 xl:grid-cols-5">
                <section className="xl:col-span-2">
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="font-semibold text-slate-900">Current {noun} catalog</h4>
                    <span className="text-xs text-slate-500">Live from {inventory.connector_name}</span>
                  </div>
                  {inventory.can_manage_catalog && (
                    <div className="mt-3 flex gap-2">
                      <input value={newName} onChange={(event) => setNewName(event.target.value)}
                             placeholder={`New ${noun} name`} maxLength={100}
                             className="min-h-touch min-w-0 flex-1 rounded-customer border border-border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2" />
                      <ActionButton type="button" onClick={createTag}
                              disabled={!newName.trim() || creating}
                              size="compact">
                        <Plus className="h-3.5 w-3.5" /> {creating ? "Adding…" : "Add"}
                      </ActionButton>
                    </div>
                  )}
                  <div className="mt-3 max-h-96 overflow-auto border border-border">
                    {inventory.catalog.map((item) => (
                      <div key={item.id} className="flex items-center justify-between gap-2 border-b border-border px-3 py-2 last:border-b-0">
                        {rename?.id === item.id ? (
                          <div className="flex min-w-0 flex-1 gap-2">
                            <input value={rename.name}
                                   onChange={(event) => setRename({ ...rename, name: event.target.value })}
                                   className="min-h-touch min-w-0 flex-1 rounded-customer border border-brand px-2 py-1 text-sm" />
                            <ActionButton type="button" onClick={saveRename} disabled={saving} size="compact">Save</ActionButton>
                            <ActionButton type="button" onClick={() => setRename(null)} variant="secondary" size="compact">Cancel</ActionButton>
                          </div>
                        ) : (
                          <>
                            <div className="min-w-0">
                              <div className="truncate text-sm font-medium text-slate-900">{item.name}</div>
                              <div className="text-[11px] text-slate-500">{item.usage_count} contact{item.usage_count === 1 ? "" : "s"}</div>
                            </div>
                            {inventory.can_manage_catalog && (
                              <div className="flex items-center gap-1">
                                <ActionButton type="button" aria-label={`Rename ${item.name}`}
                                        onClick={() => setRename({ id: item.id, name: item.name })}
                                        variant="secondary" size="icon" title={`Rename ${item.name}`}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </ActionButton>
                                <ActionButton type="button" aria-label={`Delete ${item.name}`}
                                        onClick={() => { setDeleting(item); setConfirmation(""); }}
                                        variant="destructive" size="icon" title={`Delete ${item.name}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </ActionButton>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                    {inventory.catalog.length === 0 && (
                      <div className="p-5 text-center text-sm text-slate-500">No current {noun}s. Add the first one above.</div>
                    )}
                  </div>
                </section>

                <section className="xl:col-span-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h4 className="inline-flex items-center gap-2 font-semibold text-slate-900">
                      <Users className="h-4 w-4" /> Contact assignments
                    </h4>
                    <input value={search} onChange={(event) => setSearch(event.target.value)}
                           placeholder="Search contact or tag"
                           className="min-h-touch rounded-customer border border-border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2" />
                  </div>
                  <div className="mt-3 max-h-[28rem] overflow-auto border border-border">
                    {filteredContacts.map((contact) => (
                      <div key={contact.contact_id}
                           className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-3 py-2 last:border-b-0">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-slate-900">{contact.name}</div>
                          <div className="truncate text-xs text-slate-500">{contact.email || "No email"}</div>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {(contact.tags || []).map((tag) => (
                              <span key={tag} className="border border-brand/20 bg-brand/5 px-1.5 py-0.5 text-[10px] text-brand">{tag}</span>
                            ))}
                            {contact.tags.length === 0 && <span className="text-[10px] text-slate-400">No {noun}s</span>}
                          </div>
                        </div>
                        <ActionButton type="button"
                                onClick={() => setContactEdit({
                                  ...contact, previousTags: [...contact.tags], tags: [...contact.tags],
                                })}
                                variant="secondary" size="compact">
                          Edit {noun}s
                        </ActionButton>
                      </div>
                    ))}
                  </div>
                  {inventory.contacts_truncated && (
                    <p className="mt-2 text-xs text-slate-500">
                      Showing the first 500 matching contacts. Use search to narrow the inventory.
                    </p>
                  )}
                </section>
              </div>

              <div className="border-t border-border bg-slate-50 px-4 py-3 text-xs text-slate-600">
                <AlertTriangle className="mr-1 inline h-3.5 w-3.5 text-amber-700" />
                {inventory.workflow_warning} Rename and delete only after checking provider workflows.
              </div>
            </>
          )}
        </div>
      )}

      {contactEdit && inventory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4"
             role="dialog" aria-modal="true" aria-label="Approve contact tag changes">
          <CustomerPanel className="w-full max-w-xl p-ui-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading text-lg font-bold text-slate-900">Edit {noun}s for {contactEdit.name}</h3>
                <p className="mt-1 text-sm text-slate-500">Select the exact set CleanIQ should preserve in the CRM.</p>
              </div>
              <ActionButton type="button" onClick={() => setContactEdit(null)} variant="quiet" size="icon" aria-label="Close tag editor" title="Close tag editor"><X className="h-5 w-5" /></ActionButton>
            </div>
            <div className="mt-4 grid max-h-64 grid-cols-2 gap-2 overflow-auto border border-border p-3 sm:grid-cols-3">
              {inventory.catalog.map((item) => {
                const checked = contactEdit.tags.some((tag) => tag.toLowerCase() === item.name.toLowerCase());
                return (
                  <label key={item.id} className="flex min-h-touch cursor-pointer items-center gap-2 rounded-customer border border-border p-2 text-sm">
                    <input type="checkbox" checked={checked} onChange={() => toggleContactTag(item.name)}
                           className="h-4 w-4 accent-slate-900" />
                    <span className="truncate">{item.name}</span>
                  </label>
                );
              })}
            </div>
            <div className="mt-4 border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              Approval preview: {contactEdit.previousTags.join(", ") || "No tags"} → {contactEdit.tags.join(", ") || "No tags"}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <ActionButton type="button" onClick={() => setContactEdit(null)} variant="secondary">Cancel</ActionButton>
              <ActionButton type="button" onClick={saveContactTags}
                      disabled={saving || sameTags(contactEdit.tags, contactEdit.previousTags)}
                      >
                {saving ? "Applying…" : "Apply approved tags"}
              </ActionButton>
            </div>
          </CustomerPanel>
        </div>
      )}

      {deleting && inventory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4"
             role="dialog" aria-modal="true" aria-label="Confirm tag deletion">
          <CustomerPanel className="w-full max-w-lg border-red-300 p-ui-5">
            <h3 className="font-heading text-lg font-bold text-slate-900">Delete {deleting.name}?</h3>
            <p className="mt-2 text-sm text-slate-600">
              It is assigned to {deleting.usage_count} contact{deleting.usage_count === 1 ? "" : "s"}.
              CleanIQ cannot currently prove every workflow dependency.
            </p>
            <label className="mt-4 block text-xs font-medium text-slate-700">
              Type <span className="font-mono font-bold">DELETE {deleting.name}</span> to confirm
              <input value={confirmation} onChange={(event) => setConfirmation(event.target.value)}
                     className="mt-1 min-h-touch w-full rounded-customer border border-red-300 px-3 py-2 font-mono text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500" />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <ActionButton type="button" onClick={() => setDeleting(null)} variant="secondary">Cancel</ActionButton>
              <ActionButton type="button" onClick={confirmDelete}
                      disabled={saving || confirmation !== `DELETE ${deleting.name}`}
                      variant="destructive" className="bg-red-700 text-white hover:bg-red-800">
                {saving ? "Deleting…" : "Delete from CRM"}
              </ActionButton>
            </div>
          </CustomerPanel>
        </div>
      )}
    </CustomerPanel>
  );
}
